import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "sonner";
import {
  ApiGroupResponse,
  Group,
  GroupDetails,
  GroupParams,
} from "../../types/groupAndHouses";
import GroupApis from "../../api/groupApis";
import { ResponseError, ThunkConfig } from "../../types/responseType";

interface GroupState {
  groups: Group[];
  isLoading: boolean;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
  };
  filters: {
    name: string;
  };
  groupDetails: GroupDetails | null;
}

const initialState: GroupState = {
  groups: [],
  isLoading: false,
  pagination: {
    currentPage: 0,
    pageSize: 50,
    totalElements: 0,
    totalPages: 0,
  },
  filters: {
    name: "",
  },
  groupDetails: null,
};

// ================== Async Thunk ==================
export const getGroups = createAsyncThunk<
  ApiGroupResponse["data"],
  GroupParams,
  ThunkConfig
>("group/getGroups", async (params, thunkAPI) => {
  try {
    const response = await GroupApis.getGroups(params);
    return response?.data;
  } catch (error: ResponseError | any) {
    const message =
      error?.response?.data?.error?.message || "Error fetching groups";
    return thunkAPI.rejectWithValue(message);
  }
});

export const getGroupDetails = createAsyncThunk<
  GroupDetails,
  number,
  ThunkConfig
>("group/getGroupDetails", async (groupId, thunkAPI) => {
  try {
    const response = await GroupApis.getGroupById(groupId);
    return response!;
  } catch (error: any) {
    return thunkAPI.rejectWithValue("Error fetching group details");
  }
});

// ================== Slice ==================
const groupSlice = createSlice({
  name: "group",
  initialState,
  reducers: {
    setPagination: (
      state,
      action: PayloadAction<{ currentPage: number; pageSize: number }>,
    ) => {
      state.pagination.currentPage = action.payload.currentPage;
      state.pagination.pageSize = action.payload.pageSize;
    },
    setFilters: (
      state,
      action: PayloadAction<Partial<GroupState["filters"]>>,
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearGroupDetails: (state) => {
      state.groupDetails = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGroups.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getGroups.fulfilled, (state, action) => {
        state.isLoading = false;

        state.groups = action.payload.content.map((g) => ({
          groupId: g.groupId,
          groupName: g.groupName,
          noOfStudents: g.noOfStudents ?? 0,
        }));

        state.pagination = {
          currentPage: action.payload.number,
          pageSize: action.payload.size,
          totalElements: action.payload.totalElements,
          totalPages: action.payload.totalPages,
        };
      })

      .addCase(getGroups.rejected, (state, { payload }) => {
        toast.error(payload as string);
        state.isLoading = false;
        state.groups = [];
      })
      .addCase(getGroupDetails.pending, (state) => {
        state.isLoading = true;
        state.groupDetails = null;
      })
      .addCase(getGroupDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.groupDetails = action.payload;
      })
      .addCase(getGroupDetails.rejected, (state) => {
        state.isLoading = false;
        state.groupDetails = null;
      });
  },
});

export const { setPagination, setFilters,clearGroupDetails } = groupSlice.actions;
export default groupSlice.reducer;
